<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import useAuthStore from '@/common/store/pinia/auth';
  import useTotpStore from '@/common/store/pinia/totp';
  import { get as getCookie, set as setCookie } from 'tiny-cookie';
  import useThemeStore from '@/common/store/pinia/theme';
  import SwitchToCyberFrameDialog from '@/common/components/header-alerts/SwitchToCyberFrameDialog.vue';
  import ConfigStatusPopover from '@/common/components/header-alerts/ConfigStatusPopover.vue';
  import { useRouter } from 'vue-router/composables';
  import useClusterRegistrationsStore from '@/common/store/pinia/clusterRegistrations';
  import takeFirst from '@/common/utils/takeFirst';
  import { store } from '@/common/root';
  import useFeaturesStore from '@/common/store/pinia/features';
  import ROLES from '@/common/constants/roles.constants';
  import type { Ribbon } from '@uikit/ui-kit';

  const TWO_FACTOR_ALERT_COOKIE_KEY = 'two_factor_security_alert_dismissed';

  type ShowIn = 'self-service' | 'admin-panel' | 'all';

  type Alert = {
    description: string
    type: string
    display: boolean
    showIn: ShowIn
    onClose?: () => void
  }

  const props = defineProps<{
    showIn: 'self-service' | 'admin-panel';
  }>();

  const { t } = useI18n();

  const authStore = useAuthStore();
  const totpStore = useTotpStore();
  const themeStore = useThemeStore();
  const featuresStore = useFeaturesStore();
  const router = useRouter();
  const clusterRegistrationsStore = useClusterRegistrationsStore();

  const closedAlerts = ref<number[]>([]);
  const vendorSwitcherVisible = ref(false);
  const showConfigStatusPopover = ref(false);
  const loading = ref(false);
  const ribbonRef = ref<Ribbon | null>(null);
  const hasRegistrations = computed(() => clusterRegistrationsStore.getItems.length > 0);

  const isFullyConfigured = computed(() => {
    const isComputeDeployed = store.getters['compute/isComputeConfigured'];
    const computeFeatures = store.getters['compute/computeClusterFeatures'] || {};
    const isBillingMeteringEnabled = !!computeFeatures.metering;
    const isSelfServiceEnabled = !!store.getters['rolesets/getRolesSetByRoleName']?.(ROLES.SELF_SERVICE);
    const hasSspUrl = !!clusterRegistrationsStore.config?.ssp_url;

    return isComputeDeployed && isBillingMeteringEnabled && isSelfServiceEnabled && hasSspUrl;
  });

  const displayTotpAlert = computed(() => {
    const isDismissed = Boolean(getCookie(TWO_FACTOR_ALERT_COOKIE_KEY));
    const isFederated = !!authStore.currentIdpId;

    if (isDismissed || isFederated) {
      return false;
    }

    if (!totpStore.status) {
      return false;
    }

    return !totpStore.loading
      && !totpStore.isTotpEnabled
      && !totpStore.isTotpEnforced;
  });

  const allAlerts = computed<Alert[]>(() => [
    {
      description: t('Protect your account with two-factor authentication. Enable it in your profile.'),
      type: 'info',
      display: displayTotpAlert.value,
      showIn: 'self-service',
      onClose: () => {
        setCookie(TWO_FACTOR_ALERT_COOKIE_KEY, '1', { expires: '10Y', secure: true, samesite: 'Strict' });
      },
    },
    {
      description: t('You can switch to Acronis Cyber Frame.'),
      type: 'info',
      display: themeStore.themeData.showConvertToCyberFrameBanner && featuresStore.isSwitchToCyberframeEnabled,
      showIn: 'admin-panel',
      link: {
        title: t('Yes, switch now.'),
        click: () => {
          vendorSwitcherVisible.value = true;
        }
      }
    },
    {
      description: t('Register your installation in Acronis Cyber Protect Cloud to switch to the Cyber Frame licensing model.'),
      type: 'info',
      display: themeStore.themeData.showRegisterInCyberFrameBanner && !hasRegistrations.value && !loading.value,
      showIn: 'admin-panel',
      link: {
        title: t('Go to cloud registration'),
        click: () => {
          router.push({ name: 'ClusterRegistration' });
        }
      }
    },
    {
      description: t('The cluster is registered in Acronis Cyber Protect Cloud but is not fully configured.'),
      type: 'warning',
      display: themeStore.themeData.showRegisterInCyberFrameBanner && hasRegistrations.value && !isFullyConfigured.value,
      showIn: 'admin-panel',
      link: {
        title: t('Check its status'),
        click: () => {
          showConfigStatusPopover.value = true;
        }
      }
    },
  ]);

  const filteredAlerts = computed<(Alert & { originalIndex: number })[]>(() => allAlerts.value
    .map((alert, index) => ({ ...alert, originalIndex: index }))
    .filter(alert => {
      if (!alert.display) {
        return false;
      }

      if (closedAlerts.value.includes(alert.originalIndex)) {
        return false;
      }

      return alert.showIn === 'all' || alert.showIn === props.showIn;
    }));

  const onClose = (index: number) => {
    const alert = filteredAlerts.value[index];

    if (!alert) {
      return;
    }

    if (!closedAlerts.value.includes(alert.originalIndex)) {
      closedAlerts.value.push(alert.originalIndex);
    }

    if (alert.onClose) {
      alert.onClose();
    }
  };

  onMounted(() => {
    loading.value = true;

    const promises = [
      takeFirst(() => store.dispatch('compute/FETCH_COMPUTE_CLUSTER')),
      takeFirst(featuresStore.fetchRemoteFeatureFlags)
    ];

    if (props.showIn === 'admin-panel') {
      promises.push(
        takeFirst(clusterRegistrationsStore.fetchItems),
        takeFirst(clusterRegistrationsStore.fetchConfig),
        takeFirst(() => store.dispatch('rolesets/FETCH_NETWORK_ROLE_SETS'))
      );
    }

    Promise.all(promises).finally(() => {
      loading.value = false;
    });
  });
</script>

<template>
<div data-component-name="header-alerts" class="header-alerts">
  <config-status-popover data-component-name="config-status-popover"
    v-model="showConfigStatusPopover"
    :is-fully-configured="isFullyConfigured"
    :reference-el="ribbonRef?.$el"
  />
  <el-ribbon data-component-name="el-ribbon"
    ref="ribbonRef"
    :alerts="filteredAlerts"
    @close="onClose"
  >
  </el-ribbon>
  <SwitchToCyberFrameDialog data-component-name="switch-to-cyber-frame-dialog"
    v-if="vendorSwitcherVisible"
    @close="vendorSwitcherVisible = false"
  ></SwitchToCyberFrameDialog>
</div>
</template>

<style scoped lang="scss">
.el-ribbon ::v-deep .el-link {
  cursor: pointer;
}
</style>
